/**
 * @fileoverview Trace context management functions
 * @module context
 */

import { TraceContext } from './TraceContext.js';
import { parseTraceparent } from './headers.js';
import { TRACE_HEADERS } from './constants.js';

/**
 * Current page-level trace context — the PAGE's correlation root.
 *
 * PLN-BOARD-GATE-001 I-12: this is NOT the trace an outbound request rides.
 * A trace is one request (W3C Trace Context / OpenTelemetry), so
 * `injectTraceContext` mints a fresh root — fresh `traceId`, `spanId` and
 * `requestId` — per request. What lives here is what is genuinely
 * page-scoped: the tenant, the head-based sampling decision, `tracestate`,
 * and the page's own `traceId`, which every request carries as
 * `x-correlation-id` and every shipped browser log line carries as its
 * `traceId`. Before this, one page kept ONE traceId + ONE requestId for
 * hours of API log lines, so a single request could not be told apart.
 *
 * @private
 * @type {TraceContext|null}
 */
let currentContext = null;

/**
 * Initialize or get the current trace context for the page (the correlation
 * root — see the note on `currentContext`).
 * @param {Object} [options={}] - Options
 * @param {string} [options.tenantId] - Tenant ID to include in context
 * @returns {TraceContext} Current trace context
 */
export function getTraceContext(options = {}) {
  if (!currentContext) {
    currentContext = new TraceContext(options);
  } else if (options.tenantId && !currentContext.tenantId) {
    currentContext.tenantId = options.tenantId;
  }
  return currentContext;
}

/**
 * Create a new trace context for a specific operation
 * @param {Object} [options={}] - Options
 * @returns {TraceContext} New trace context
 */
export function createTraceContext(options = {}) {
  return new TraceContext(options);
}

/**
 * Reset the current trace context (e.g., on page navigation)
 */
export function resetTraceContext() {
  currentContext = null;
}

/**
 * Inject trace context headers into an HTTP request config
 * Use with axios or fetch requests.
 *
 * Mints a NEW trace per request (fresh `traceId` / `spanId` / `requestId`),
 * carrying the page context's tenant, sampling decision and `tracestate`, and
 * the page's `traceId` as `x-correlation-id` (PLN-BOARD-GATE-001 I-12).
 *
 * @param {Object} [config={}] - Request config (axios-style)
 * @param {Object} [options={}] - Options
 * @param {string} [options.tenantId] - Tenant ID to include
 * @returns {Object} Updated config with trace headers
 */
export function injectTraceContext(config = {}, options = {}) {
  const page = getTraceContext(options);
  const context = createTraceContext({
    tenantId: page.tenantId,
    sampled: page.sampled,
    traceState: page.traceState,
    correlationId: page.traceId,
  });

  return {
    ...config,
    headers: {
      ...config.headers,
      ...context.toHeaders(),
    },
  };
}

/**
 * Extract trace context from response headers
 * @param {Object|Headers} headers - Response headers (object or Headers instance)
 * @returns {Object} Extracted trace context data
 */
export function extractTraceContext(headers) {
  const get = (key) => {
    if (headers instanceof Headers) {
      return headers.get(key);
    }
    return headers[key] || headers[key.toLowerCase()];
  };

  const traceparent = get(TRACE_HEADERS.TRACEPARENT);
  const parsed = traceparent ? parseTraceparent(traceparent) : null;

  return {
    traceId: parsed?.traceId || get(TRACE_HEADERS.TRACE_ID) || null,
    spanId: parsed?.spanId || get(TRACE_HEADERS.SPAN_ID) || null,
    requestId: get(TRACE_HEADERS.REQUEST_ID) || null,
    tenantId: get(TRACE_HEADERS.TENANT_ID) || null,
    sampled: parsed?.sampled ?? true,
  };
}
